import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatIDR } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MagnifyingGlass, CheckCircle, Clock, XCircle, CreditCard } from "@phosphor-icons/react";

const STATUS_META = {
  new_inquiry: { label_id: "Menunggu Konfirmasi", label_en: "Awaiting Confirmation", tone: "bg-blue-50 text-blue-700 border-blue-200", Icon: Clock },
  confirmed: { label_id: "Terkonfirmasi — Bayar Deposit", label_en: "Confirmed — Pay Deposit", tone: "bg-amber-50 text-amber-700 border-amber-200", Icon: CheckCircle },
  deposit_paid: { label_id: "Deposit Diterima", label_en: "Deposit Received", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle },
  completed: { label_id: "Selesai", label_en: "Completed", tone: "bg-zinc-100 text-zinc-700 border-zinc-200", Icon: CheckCircle },
  cancelled: { label_id: "Dibatalkan", label_en: "Cancelled", tone: "bg-red-50 text-red-700 border-red-200", Icon: XCircle },
};

export default function ReservationStatus() {
  const { booking_number: initialBn } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [bn, setBn] = useState(initialBn || "");
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  React.useEffect(() => { if (initialBn) doLookup(initialBn); /* eslint-disable-next-line */ }, [initialBn]);

  const doLookup = async (query) => {
    const q = (query || bn).trim().toUpperCase();
    if (!q) return;
    setLoading(true);
    try {
      const r = await api.get(`/reservations/lookup/${encodeURIComponent(q)}`);
      setReservation(r.data);
    } catch {
      toast.error(lang === "id" ? "Reservasi tidak ditemukan" : "Reservation not found");
      setReservation(null);
    } finally { setLoading(false); }
  };

  const payDeposit = async () => {
    setPaying(true);
    try {
      const r = await api.post(`/reservations/${reservation.id}/pay-deposit`);
      setReservation(r.data);
      toast.success(lang === "id" ? "Deposit diterima!" : "Deposit received!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setPaying(false); }
  };

  const meta = reservation ? STATUS_META[reservation.status] : null;

  return (
    <div className="max-w-3xl mx-auto px-6 pt-20 pb-24">
      <p className="label-eyebrow text-[hsl(var(--sk-primary))]">{lang === "id" ? "Cek Reservasi" : "Check Reservation"}</p>
      <h1 className="mt-4 font-display text-5xl sm:text-6xl tracking-tighter">
        {lang === "id" ? "Status Reservasi Anda" : "Your Reservation Status"}
      </h1>

      <div className="mt-10 flex gap-3">
        <Input value={bn} onChange={(e) => setBn(e.target.value)} placeholder="EVT-YYMMDD-XXXXXX"
          className="uppercase" data-testid="lookup-input" onKeyDown={(e) => e.key === "Enter" && doLookup()} />
        <Button onClick={() => doLookup()} disabled={loading} data-testid="lookup-btn"
          className="rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90 text-white">
          <MagnifyingGlass size={16} className="mr-2" /> {lang === "id" ? "Cari" : "Look up"}
        </Button>
      </div>

      {reservation && meta && (
        <div className="mt-12 bg-white rounded-2xl border border-[hsl(var(--sk-border))] p-8 lg:p-10" data-testid="reservation-card">
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{lang === "id" ? "Nomor Booking" : "Booking Number"}</p>
              <p className="font-mono-stat text-2xl mt-1" data-testid="resv-booking-no">{reservation.booking_number}</p>
            </div>
            <Badge variant="outline" className={`${meta.tone} gap-1 py-1.5 px-3`} data-testid="resv-status">
              <meta.Icon size={14} /> {lang === "id" ? meta.label_id : meta.label_en}
            </Badge>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 py-6 border-y border-[hsl(var(--sk-border))]">
            <Row label={lang === "id" ? "Nama" : "Name"} value={reservation.customer_name} />
            <Row label={lang === "id" ? "Tanggal Acara" : "Event Date"} value={reservation.event_date} mono />
            <Row label={lang === "id" ? "Jam" : "Time"} value={`${reservation.start_time}–${reservation.end_time}`} mono />
            <Row label={lang === "id" ? "Tamu" : "Guests"} value={reservation.guest_count} />
            <Row label={lang === "id" ? "Paket" : "Package"} value={reservation.package} className="capitalize" />
            <Row label={lang === "id" ? "Jenis" : "Type"} value={reservation.event_type} />
          </div>

          <div className="grid sm:grid-cols-2 gap-6 py-6">
            <div>
              <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{lang === "id" ? "Estimasi Total" : "Quoted Total"}</p>
              <p className="mt-2 font-mono-stat text-xl">{formatIDR(reservation.quoted_price)}</p>
            </div>
            <div>
              <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{lang === "id" ? "Deposit (50%)" : "Deposit (50%)"}</p>
              <p className="mt-2 font-mono-stat text-xl">{formatIDR(reservation.deposit_amount)}</p>
            </div>
          </div>

          {reservation.status === "new_inquiry" && (
            <p className="mt-4 text-sm text-[hsl(var(--sk-muted-fg))]">
              {lang === "id"
                ? "Tim kami akan meninjau permintaan Anda dan mengkonfirmasi dalam 24 jam. Setelah dikonfirmasi, Anda dapat kembali ke halaman ini untuk membayar deposit."
                : "Our team will review and confirm your request within 24 hours. Once confirmed, return to this page to pay your deposit."}
            </p>
          )}

          {reservation.status === "confirmed" && (
            <div className="mt-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between p-6 bg-[hsl(var(--sk-secondary))] rounded-xl">
              <div>
                <p className="text-sm font-medium">
                  {lang === "id" ? `Bayar deposit ${formatIDR(reservation.deposit_amount)} untuk mengunci tanggal.` : `Pay ${formatIDR(reservation.deposit_amount)} deposit to secure this date.`}
                </p>
                <p className="text-xs text-[hsl(var(--sk-muted-fg))] mt-1">{lang === "id" ? "Pembayaran akan disimulasikan (MVP)." : "Payment simulated (MVP)."}</p>
              </div>
              <Button onClick={payDeposit} disabled={paying} size="lg" data-testid="pay-deposit-btn"
                className="rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90 text-white">
                <CreditCard size={16} className="mr-2" />
                {paying ? "…" : (lang === "id" ? "Bayar Deposit (Mock)" : "Pay Deposit (Mock)")}
              </Button>
            </div>
          )}

          {reservation.status === "deposit_paid" && (
            <p className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              {lang === "id"
                ? "Deposit telah diterima. Sisa pembayaran dilakukan di lokasi pada hari acara. Sampai jumpa!"
                : "Deposit received. Remaining balance is settled onsite on the event day. See you soon!"}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono, className = "" }) {
  return (
    <div>
      <p className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{label}</p>
      <p className={`mt-2 ${mono ? "font-mono-stat" : ""} ${className}`}>{value}</p>
    </div>
  );
}
